import type ReportingUiScopeMathsStudentSummaryIndexRoute from 'teacher-ui-client/reporting/ui-scope/maths/student-summary/index/route'
import type ReportingUiScopeMathsStudentSummaryIndexController from 'teacher-ui-client/reporting/ui-scope/maths/student-summary/index/controller'
import { ReportingTeacherUiHeader } from 'teacher-ui-client/components/reporting/teacher-ui-header'
import { ReportingStudentSummaryControls } from 'teacher-ui-client/components/reporting/student-summary-controls'
import { ReportingNoDataMessageIf } from 'teacher-ui-client/components/reporting/no-data-message-if'
import { StudentChartWrapper } from 'teacher-ui-client/components/student-chart-wrapper'
import { CourseProgressStudent } from 'teacher-ui-client/components/giraffe/charts/course-progress/student'
import { AssessmentScoresStudent } from 'teacher-ui-client/components/giraffe/charts/assessment-scores/student'
import { RadioButtonGroup } from 'teacher-ui-client/components/base/radio-button-group'
import { DrivingTestsStudent } from 'teacher-ui-client/components/giraffe/charts/driving-tests/student'
import { AwardsEarnedCertificatesTotals } from 'teacher-ui-client/components/giraffe/charts/awards-earned/certificates/totals'
import { Precinct } from 'teacher-ui-client/utils/precincts'
import { array } from '@ember/helper'
import { ExportType } from 'teacher-ui-client/components/reporting/export-select'
import { not } from 'ember-truth-helpers'
import RouteTemplate from 'ember-route-template'
import { t } from 'ember-intl'
import { isEmpty } from '@ember/utils'
import Component from '@glimmer/component'
import { service } from '@ember/service'
import type MathsDrivingTestsService from 'teacher-ui-client/services/maths-driving-tests'

interface Signature {
  Args: {
    model: ModelFor<ReportingUiScopeMathsStudentSummaryIndexRoute>
    controller: ReportingUiScopeMathsStudentSummaryIndexController
  }
}

export class ReportingUiScopeMathsStudentSummaryIndexPage extends Component<Signature> {
  @service mathsDrivingTests!: MathsDrivingTestsService

  get pageTitle() {
    const { student } = this.args.model
    const studentName = student && student.fullName
    return `Student results ${studentName}`
  }

  get drivingTestsChart() {
    return this.args.model.drivingTestsStudent?.find(
      (d) => d.driving_test_grade === this.args.controller.drivingTestsGrade,
    )
  }

  get drivingTestsGradeButtons() {
    return this.mathsDrivingTests.drivingTestData.map((dtData) => ({
      text: dtData.fullName,
      value: dtData.drivingTestGrade, // 0, 1 or 2
    }))
  }

  get noStudentData() {
    return (
      isEmpty(this.args.model.courseProgressStudent) &&
      isEmpty(this.args.model.assessmentScoresStudent) &&
      isEmpty(this.args.model.awardsEarnedByMonthEvents) &&
      isEmpty(this.args.model.drivingTestsStudent)
    )
  }

  // Checks all the charts for data, if no data this disables the export button
  get anyDataCheck() {
    return this.args.model.students && !this.noStudentData
  }

  <template>
    <ReportingTeacherUiHeader @title={{this.pageTitle}} />
    <ReportingStudentSummaryControls
      @students={{@model.students}}
      @student={{@model.student}}
      @routePrefix="reporting.ui-scope.maths.student-summary"
      @exportOptions={{array ExportType.print}}
      @isDataPresent={{this.anyDataCheck}}
      @studentRouteName="reporting.ui-scope.maths.student-summary"
      @allStudentsRouteName="reporting.ui-scope.maths.dashboard"
    />

    <ReportingNoDataMessageIf @on={{this.noStudentData}}>
      <StudentChartWrapper @empty={{not @model.courseProgressStudent}}>
        {{#if @model.courseProgressStudent}}
          <CourseProgressStudent
            @data={{@model.courseProgressStudent}}
            @precinct={{Precinct.MS_LESSONS}}
            @summaryTitle="Lesson progress"
            @period={{@model.period}}
          />
        {{/if}}
      </StudentChartWrapper>
      <StudentChartWrapper @empty={{not @model.assessmentScoresStudent}}>
        {{#if @model.assessmentScoresStudent}}
          <AssessmentScoresStudent
            @data={{@model.assessmentScoresStudent}}
            @summaryTitle={{t "reporting.summaryTitles.assessmentScoresStudent.title.maths"}}
          />
        {{/if}}
      </StudentChartWrapper>

      <RadioButtonGroup
        data-test-driving-test-grade-select
        class="-mb-6"
        @name="driving-test-grade-select"
        @buttons={{this.drivingTestsGradeButtons}}
        @value={{@controller.drivingTestsGrade}}
        @onSelect={{@controller.setDrivingTestsGrade}}
      />
      <StudentChartWrapper @empty={{not this.drivingTestsChart}}>
        {{#if this.drivingTestsChart}}
          <DrivingTestsStudent @data={{this.drivingTestsChart}} @product={{@model.product}} />
        {{/if}}
      </StudentChartWrapper>

      <StudentChartWrapper @empty={{not @model.awardsEarnedByMonthEvents}}>
        {{#if @model.awardsEarnedByMonthEvents}}
          <AwardsEarnedCertificatesTotals
            data-test-awards-earned-certificates-by-month
            class="relative"
            @data={{@model.awardsEarnedByMonthEvents}}
            @product={{@model.product}}
            @title={{t "reporting.chartTitles.awardsEarnedByMonthEvents"}}
            @summaryTitle={{t "reporting.summaryTitles.awardsEarnedByMonthEvents"}}
            @yAxisTitle={{t "reporting.yAxisLabels.awardsEarnedByMonthEvents"}}
          />
        {{/if}}
      </StudentChartWrapper>
    </ReportingNoDataMessageIf>
  </template>
}

export default RouteTemplate(ReportingUiScopeMathsStudentSummaryIndexPage)
